import { getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPIconButton, IPScrollablePane, IPSeparator, IPText } from 'baseComponents';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import SceneLoader from 'components/Loading/SceneLoader';
import { globalStackStylesResourcePanel, globalStackTokensGapMedium, globalTextStylesBold } from 'globalStyles';
import { ReactNode, Suspense, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import CollapsedResourcePanel from './CollapsedResourcePanel';
import MobileResourcePanel from './MobileResourcePanel';

export interface IResourcePanelProps {
  title?: string;

  /**
   * The open panel's width in px. Absent is the 500 every detail scene has always used; a host only
   * passes this when its own reading column has to survive the panel on a narrower viewport.
   *
   * DESKTOP ONLY. Below the app's mobile width the panel is an `IPPanel` that owns its own width -
   * see `MobileResourcePanel`, which ignores this deliberately.
   */
  width?: number;

  children: ReactNode;
  scrollable?: boolean;
}

/** The width an open panel takes when its host names none. */
const resourcePanelDefaultWidth: number = 500;

const ResourcePanel = (props: IResourcePanelProps) => {
  const appContext = useContext(AppContext);
  const [show, setShow] = useState<boolean>(appContext.showResourcePanel);
  const { t } = useTranslation(['translation']);
  const theme = getTheme();
  const border = `1px solid ${appContext.useDarkMode ? theme.palette.neutralDark : theme.palette.neutralLighter}`;

  useEffect(() => {
    setShow(appContext.showResourcePanel);
  }, [appContext.showResourcePanel]);

  useEffect(() => {
    appContext.setShowResourcePanel(show);
    setLocalStorageData(appContext, LocalStorageKeys.ShowResourcePanel, show.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  //
  // Main render
  //
  return (
    <div
      style={{
        contain: 'layout',
        height: '100%',
        width: show ? props.width ?? resourcePanelDefaultWidth : appContext.isMobileView ? 3 : 10,
        transition: appContext.isMobileView ? undefined : 'width 0.5s ease',
      }}
    >
      {!show && <CollapsedResourcePanel onShow={() => setShow(true)} />}
      {show && appContext.isMobileView && <MobileResourcePanel {...props} onClose={() => setShow(false)} />}
      {show && !appContext.isMobileView && (
        <IPStack horizontal styles={{ root: { height: '100%', width: '100%' } }}>
          <IPStack.Item styles={{ root: { width: 5 } }}>
            <IPIconButton
              iconProps={{ iconName: 'ChevronRight' }}
              onClick={() => setShow(false)}
              styles={{
                root: {
                  left: -7,
                  top: +25,
                  border: border,
                  background: appContext.useDarkMode ? theme.palette.neutralDark : theme.palette.white,
                  zIndex: 651, //below LayerHost of panels (800+)
                },
              }}
            />
          </IPStack.Item>

          {/* Zindex just below the button */}
          <IPSeparator vertical styles={{ root: { zIndex: 650 } }} />

          <IPStack.Item grow styles={{ root: { minWidth: 0, overflowX: 'hidden' } }}>
            <Suspense fallback={<SceneLoader />}>
              <IPStack verticalFill styles={globalStackStylesResourcePanel} tokens={globalStackTokensGapMedium}>
                <IPStack.Item>
                  <IPStack horizontal horizontalAlign={'space-between'}>
                    <IPText styles={globalTextStylesBold} variant="large">
                      {props.title || t('translation:ResourcePanel.Title')}
                    </IPText>
                  </IPStack>
                </IPStack.Item>
                <IPStack.Item grow styles={{ root: { minHeight: 0, position: 'relative' } }}>
                  {props.scrollable !== false ? (
                    <IPScrollablePane
                      scrollbarVisibility={IPScrollbarVisibility.auto}
                      styles={{ contentContainer: { overflowX: 'hidden' } }}
                    >
                      {props.children}
                    </IPScrollablePane>
                  ) : (
                    <IPStack verticalFill styles={{ root: { minHeight: 0, overflow: 'hidden' } }}>
                      {props.children}
                    </IPStack>
                  )}
                </IPStack.Item>
              </IPStack>
            </Suspense>
          </IPStack.Item>
        </IPStack>
      )}
    </div>
  );
};

export default ResourcePanel;
